import React, { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIndicatorIcon,
  Edit as EditIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useSnackbar } from 'notistack';
import Can from '../../components/Can';
import crmCatalogoService from '../../services/crmCatalogoService';
import crmPipelineService from '../../services/crmPipelineService';
import type {
  CrmMotivoPerdida,
  CrmOrigen,
  CrmPipeline,
  CrmPipelineEtapaCreate,
} from '../../types/crm';
import { getApiErrorMessage } from '../../utils/apiError';

interface TabPanelProps {
  value: number;
  index: number;
  children: React.ReactNode;
}

type EtapaFormRow = CrmPipelineEtapaCreate & { clientKey: string };

const TabPanel: React.FC<TabPanelProps> = ({ value, index, children }) =>
  value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;

let etapaKeySeq = 0;
const nextEtapaKey = (): string => `etapa-${Date.now()}-${++etapaKeySeq}`;

const emptyEtapa = (orden: number): EtapaFormRow => ({
  clientKey: nextEtapaKey(),
  nombre: '',
  orden,
  probabilidadDefault: null,
  color: '#1976d2',
  esCierreGanado: false,
  esCierrePerdido: false,
});

const toDtoEtapas = (rows: EtapaFormRow[]): CrmPipelineEtapaCreate[] =>
  rows.map(({ clientKey: _clientKey, ...rest }, idx) => ({
    ...rest,
    orden: idx + 1,
  }));

interface SortableEtapaRowProps {
  etapa: EtapaFormRow;
  index: number;
  total: number;
  onChange: (index: number, patch: Partial<EtapaFormRow>) => void;
  onRemove: (index: number) => void;
}

const SortableEtapaRow: React.FC<SortableEtapaRowProps> = ({
  etapa,
  index,
  total,
  onChange,
  onRemove,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: etapa.clientKey,
  });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.85 : 1,
    zIndex: isDragging ? 1 : undefined,
    backgroundColor: isDragging ? '#f7fbff' : undefined,
    borderRadius: 8,
    padding: isDragging ? 4 : 0,
  };

  return (
    <Stack
      ref={setNodeRef}
      style={style}
      direction={{ xs: 'column', md: 'row' }}
      spacing={1}
      alignItems={{ md: 'center' }}
    >
      <IconButton
        size="small"
        title="Arrastrar para reordenar"
        aria-label="Arrastrar etapa"
        sx={{ cursor: 'grab', touchAction: 'none', flexShrink: 0 }}
        {...attributes}
        {...listeners}
      >
        <DragIndicatorIcon fontSize="small" />
      </IconButton>
      <TextField
        label="Nombre"
        size="small"
        value={etapa.nombre}
        onChange={(e) => onChange(index, { nombre: e.target.value })}
        sx={{ flex: 1 }}
      />
      <TextField
        label="Prob. %"
        size="small"
        type="number"
        value={etapa.probabilidadDefault ?? ''}
        onChange={(e) =>
          onChange(index, {
            probabilidadDefault: e.target.value === '' ? null : Number(e.target.value),
          })
        }
        sx={{ width: 100 }}
      />
      <TextField
        label="Color"
        size="small"
        type="color"
        value={etapa.color || '#1976d2'}
        onChange={(e) => onChange(index, { color: e.target.value })}
        sx={{ width: 90 }}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={Boolean(etapa.esCierreGanado)}
            onChange={(e) => onChange(index, { esCierreGanado: e.target.checked })}
          />
        }
        label="Ganado"
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={Boolean(etapa.esCierrePerdido)}
            onChange={(e) => onChange(index, { esCierrePerdido: e.target.checked })}
          />
        }
        label="Perdido"
      />
      <IconButton
        size="small"
        onClick={() => onRemove(index)}
        disabled={total <= 1}
        aria-label="Eliminar etapa"
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Stack>
  );
};

const CrmConfig: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(0);
  const [pipelines, setPipelines] = useState<CrmPipeline[]>([]);
  const [origenes, setOrigenes] = useState<CrmOrigen[]>([]);
  const [motivos, setMotivos] = useState<CrmMotivoPerdida[]>([]);
  const [loading, setLoading] = useState(false);

  const [pipelineDialog, setPipelineDialog] = useState(false);
  const [editingPipelineId, setEditingPipelineId] = useState<number | null>(null);
  const [pipeNombre, setPipeNombre] = useState('');
  const [pipeActivo, setPipeActivo] = useState(true);
  const [pipeOrden, setPipeOrden] = useState(1);
  const [etapas, setEtapas] = useState<EtapaFormRow[]>([emptyEtapa(1)]);
  const [saving, setSaving] = useState(false);

  const [catalogDialog, setCatalogDialog] = useState<'origen' | 'motivo' | null>(null);
  const [catalogEditId, setCatalogEditId] = useState<number | null>(null);
  const [catalogNombre, setCatalogNombre] = useState('');
  const [catalogActivo, setCatalogActivo] = useState(true);
  const [catalogOrden, setCatalogOrden] = useState(0);

  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      const [p, o, m] = await Promise.all([
        crmPipelineService.getAll(),
        crmCatalogoService.getOrigenes(),
        crmCatalogoService.getMotivosPerdida(),
      ]);
      setPipelines(p ?? []);
      setOrigenes(o ?? []);
      setMotivos(m ?? []);
      return p ?? [];
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al cargar configuración'), {
        variant: 'error',
      });
      return [] as CrmPipeline[];
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  const openEditPipeline = useCallback((p: CrmPipeline) => {
    setEditingPipelineId(p.id);
    setPipeNombre(p.nombre);
    setPipeActivo(p.activo);
    setPipeOrden(p.orden);
    setEtapas(
      [...(p.etapas ?? [])]
        .sort((a, b) => a.orden - b.orden)
        .map((e) => ({
          clientKey: e.id != null ? `id-${e.id}` : nextEtapaKey(),
          id: e.id,
          nombre: e.nombre,
          orden: e.orden,
          probabilidadDefault: e.probabilidadDefault,
          color: e.color,
          esCierreGanado: e.esCierreGanado,
          esCierrePerdido: e.esCierrePerdido,
        })),
    );
    setTab(0);
    setPipelineDialog(true);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const loaded = await loadAll();
      if (cancelled) return;
      const editIdRaw = searchParams.get('editPipelineId');
      if (!editIdRaw) return;
      const editId = Number(editIdRaw);
      if (Number.isNaN(editId)) return;
      const pipe = loaded.find((p) => p.id === editId);
      if (pipe) openEditPipeline(pipe);
      const next = new URLSearchParams(searchParams);
      next.delete('editPipelineId');
      setSearchParams(next, { replace: true });
    })();
    return () => {
      cancelled = true;
    };
    // Solo al montar / si cambia el query de edición
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openNewPipeline = () => {
    setEditingPipelineId(null);
    setPipeNombre('');
    setPipeActivo(true);
    setPipeOrden((pipelines.length || 0) + 1);
    setEtapas([
      { ...emptyEtapa(1), nombre: 'Nuevo' },
      { ...emptyEtapa(2), nombre: 'Calificado' },
      { ...emptyEtapa(3), nombre: 'Propuesta' },
      { ...emptyEtapa(4), nombre: 'Ganado', esCierreGanado: true, color: '#2e7d32' },
      { ...emptyEtapa(5), nombre: 'Perdido', esCierrePerdido: true, color: '#d32f2f' },
    ]);
    setPipelineDialog(true);
  };

  const savePipeline = async () => {
    if (!pipeNombre.trim()) {
      enqueueSnackbar('El nombre del pipeline es obligatorio', { variant: 'warning' });
      return;
    }
    const etapasValidas = toDtoEtapas(etapas.filter((e) => e.nombre.trim())).map((e) => ({
      ...e,
      nombre: e.nombre.trim(),
    }));
    if (!etapasValidas.length) {
      enqueueSnackbar('Agregá al menos una etapa', { variant: 'warning' });
      return;
    }
    setSaving(true);
    try {
      const dto = {
        nombre: pipeNombre.trim(),
        activo: pipeActivo,
        orden: pipeOrden,
        etapas: etapasValidas,
      };
      if (editingPipelineId == null) {
        await crmPipelineService.create(dto);
        enqueueSnackbar('Pipeline creado', { variant: 'success' });
      } else {
        await crmPipelineService.update(editingPipelineId, dto);
        enqueueSnackbar('Pipeline actualizado', { variant: 'success' });
      }
      setPipelineDialog(false);
      await loadAll();
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al guardar pipeline'), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const openCatalog = (tipo: 'origen' | 'motivo', item?: CrmOrigen | CrmMotivoPerdida) => {
    setCatalogDialog(tipo);
    setCatalogEditId(item?.id ?? null);
    setCatalogNombre(item?.nombre ?? '');
    setCatalogActivo(item?.activo ?? true);
    setCatalogOrden(item?.orden ?? 0);
  };

  const etapaIds = useMemo(() => etapas.map((e) => e.clientKey), [etapas]);

  const etapaSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const updateEtapa = (index: number, patch: Partial<EtapaFormRow>) => {
    setEtapas((prev) => prev.map((e, i) => (i === index ? { ...e, ...patch } : e)));
  };

  const removeEtapa = (index: number) => {
    setEtapas((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const handleEtapaDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setEtapas((prev) => {
      const oldIndex = prev.findIndex((e) => e.clientKey === String(active.id));
      const newIndex = prev.findIndex((e) => e.clientKey === String(over.id));
      if (oldIndex < 0 || newIndex < 0) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const saveCatalog = async () => {
    if (!catalogNombre.trim() || !catalogDialog) return;
    setSaving(true);
    try {
      const dto = {
        nombre: catalogNombre.trim(),
        activo: catalogActivo,
        orden: catalogOrden,
      };
      if (catalogDialog === 'origen') {
        if (catalogEditId == null) await crmCatalogoService.createOrigen(dto);
        else await crmCatalogoService.updateOrigen(catalogEditId, dto);
      } else {
        if (catalogEditId == null) await crmCatalogoService.createMotivoPerdida(dto);
        else await crmCatalogoService.updateMotivoPerdida(catalogEditId, dto);
      }
      enqueueSnackbar('Guardado', { variant: 'success' });
      setCatalogDialog(null);
      await loadAll();
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al guardar'), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card
        sx={{
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Can permiso="crm.pipeline">
            <Stack spacing={2}>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                Configuración CRM
              </Typography>

              <Tabs value={tab} onChange={(_, v) => setTab(v)}>
                <Tab label="Pipelines / Etapas" />
                <Tab label="Motivos de pérdida" />
                <Tab label="Orígenes" />
              </Tabs>

              <TabPanel value={tab} index={0}>
                <Stack spacing={2}>
                  <Box>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={openNewPipeline}>
                      Nuevo pipeline
                    </Button>
                  </Box>
                  {loading && (
                    <Typography variant="body2" color="text.secondary">
                      Cargando...
                    </Typography>
                  )}
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Nombre</TableCell>
                        <TableCell>Orden</TableCell>
                        <TableCell>Activo</TableCell>
                        <TableCell>Etapas</TableCell>
                        <TableCell align="right">Acciones</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pipelines.map((p) => (
                        <TableRow key={p.id}>
                          <TableCell>{p.nombre}</TableCell>
                          <TableCell>{p.orden}</TableCell>
                          <TableCell>{p.activo ? 'Sí' : 'No'}</TableCell>
                          <TableCell>
                            {[...(p.etapas ?? [])]
                              .sort((a, b) => a.orden - b.orden)
                              .map((e) => e.nombre)
                              .join(' → ')}
                          </TableCell>
                          <TableCell align="right">
                            <IconButton size="small" onClick={() => openEditPipeline(p)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Stack>
              </TabPanel>

              <TabPanel value={tab} index={1}>
                <Stack spacing={2}>
                  <Box>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => openCatalog('motivo')}
                    >
                      Nuevo motivo
                    </Button>
                  </Box>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Nombre</TableCell>
                        <TableCell>Orden</TableCell>
                        <TableCell>Activo</TableCell>
                        <TableCell align="right">Acciones</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {motivos.map((m) => (
                        <TableRow key={m.id}>
                          <TableCell>{m.nombre}</TableCell>
                          <TableCell>{m.orden}</TableCell>
                          <TableCell>{m.activo ? 'Sí' : 'No'}</TableCell>
                          <TableCell align="right">
                            <IconButton size="small" onClick={() => openCatalog('motivo', m)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Stack>
              </TabPanel>

              <TabPanel value={tab} index={2}>
                <Stack spacing={2}>
                  <Box>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => openCatalog('origen')}
                    >
                      Nuevo origen
                    </Button>
                  </Box>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Nombre</TableCell>
                        <TableCell>Orden</TableCell>
                        <TableCell>Activo</TableCell>
                        <TableCell align="right">Acciones</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {origenes.map((o) => (
                        <TableRow key={o.id}>
                          <TableCell>{o.nombre}</TableCell>
                          <TableCell>{o.orden}</TableCell>
                          <TableCell>{o.activo ? 'Sí' : 'No'}</TableCell>
                          <TableCell align="right">
                            <IconButton size="small" onClick={() => openCatalog('origen', o)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Stack>
              </TabPanel>
            </Stack>
          </Can>
        </CardContent>
      </Card>

      <Dialog
        open={pipelineDialog}
        onClose={() => setPipelineDialog(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{editingPipelineId == null ? 'Nuevo pipeline' : 'Editar pipeline'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Nombre"
                fullWidth
                value={pipeNombre}
                onChange={(e) => setPipeNombre(e.target.value)}
              />
              <TextField
                label="Orden"
                type="number"
                value={pipeOrden}
                onChange={(e) => setPipeOrden(Number(e.target.value))}
                sx={{ width: 120 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={pipeActivo}
                    onChange={(e) => setPipeActivo(e.target.checked)}
                  />
                }
                label="Activo"
              />
            </Stack>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Etapas
            </Typography>
            <DndContext
              sensors={etapaSensors}
              collisionDetection={closestCenter}
              onDragEnd={handleEtapaDragEnd}
            >
              <SortableContext items={etapaIds} strategy={verticalListSortingStrategy}>
                <Stack spacing={1.25}>
                  {etapas.map((et, idx) => (
                    <SortableEtapaRow
                      key={et.clientKey}
                      etapa={et}
                      index={idx}
                      total={etapas.length}
                      onChange={updateEtapa}
                      onRemove={removeEtapa}
                    />
                  ))}
                </Stack>
              </SortableContext>
            </DndContext>
            <Button
              startIcon={<AddIcon />}
              onClick={() => setEtapas([...etapas, emptyEtapa(etapas.length + 1)])}
            >
              Agregar etapa
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPipelineDialog(false)}>Cancelar</Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={savePipeline}
            disabled={saving}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={catalogDialog != null} onClose={() => setCatalogDialog(null)} fullWidth maxWidth="xs">
        <DialogTitle>
          {catalogEditId == null ? 'Nuevo' : 'Editar'}{' '}
          {catalogDialog === 'origen' ? 'origen' : 'motivo'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Nombre"
              fullWidth
              value={catalogNombre}
              onChange={(e) => setCatalogNombre(e.target.value)}
            />
            <TextField
              label="Orden"
              type="number"
              value={catalogOrden}
              onChange={(e) => setCatalogOrden(Number(e.target.value))}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={catalogActivo}
                  onChange={(e) => setCatalogActivo(e.target.checked)}
                />
              }
              label="Activo"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCatalogDialog(null)}>Cancelar</Button>
          <Button variant="contained" onClick={saveCatalog} disabled={saving}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CrmConfig;
